import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { ProfileLink } from '@root/features/profile'
import { profileCoinsModal } from '@root/features/profile/profile-coins/atoms/reatom-profile-coins-modal'
import { useTranslation } from '@root/shared/localization'
import { Separator, Skeleton } from '@root/shared/ui-kit'

import { userConnectionsFollowersModal } from '../../atoms/user-connections-followers-modal'
import { userConnectionsFollowingsModal } from '../../atoms/user-connections-followings-modal'

import type { DelugeId } from '@root/entities/user-short'
import type { UserShortAvatar } from '@root/entities/user-short/ui/user-short-avatar'

type UserFollowerItemProps = {
  avatar: React.ReactElement<typeof UserShortAvatar>
  children: React.ReactNode
  name: string
  delugeId: DelugeId
  followers: number
  following: number
  coins: number | undefined
  onNavigate?: () => void
}

export const UserConnectionsItem = ({
  avatar,
  children,
  name,
  delugeId,
  followers,
  following,
  coins,
  onNavigate,
}: UserFollowerItemProps) => {
  const { t } = useTranslation()

  const [_followersModal, openFollowersModal] = useAtom(
    userConnectionsFollowersModal,
  )
  const [_followingsModal, openFollowingsModal] = useAtom(
    userConnectionsFollowingsModal,
  )

  const [_profileCoinsModal, setProfileCoinsModal] = useAtom(profileCoinsModal)

  return (
    <Root>
      <UserProfile>
        <div>
          <ProfileLink onClick={onNavigate} delugeId={delugeId}>
            {avatar}
          </ProfileLink>
        </div>
        {name ? (
          <ProfileLink onClick={onNavigate} delugeId={delugeId}>
            <Name>{name}</Name>
          </ProfileLink>
        ) : undefined}
      </UserProfile>
      <Center>
        <LabeledCount
          delugeId={delugeId}
          label={t('common.follower_other')}
          count={followers}
          countMinChars={3}
          onClick={() => {
            openFollowersModal({ delugeId, followers })
          }}
        />
        <Sep vertical />
        <LabeledCount
          delugeId={delugeId}
          label={t('common.following_other')}
          count={following}
          countMinChars={3}
          onClick={() => {
            openFollowingsModal({ delugeId, followings: following })
          }}
        />
        {coins && coins > 0 ? (
          <>
            <Sep vertical />
            <LabeledCount
              delugeId={delugeId}
              label={t('common.created_coins')}
              count={coins}
              countMinChars={3}
              onClick={() => {
                setProfileCoinsModal({ delugeId })
              }}
            />
          </>
        ) : undefined}
      </Center>
      <Right>{children}</Right>
    </Root>
  )
}

const Root = styled.div`
  padding: 12px 14px;
  gap: 16px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  background: var(--background-2);
  border-radius: 10px;
  align-items: center;
`

const UserProfile = styled.div`
  color: var(--text-12);

  gap: 8px;
  display: flex;
  align-items: center;
`

const Center = styled.div`
  display: flex;
  height: 100%;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  color: var(--text-9);
`
const Right = styled.div`
  gap: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  word-break: break-word;
`
const Name = styled.div`
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  text-wrap: balance;
  color: var(--appearance-inv-1);
`
const Sep = styled(Separator)`
  height: 14px;
  background: var(--background-7);
`

const LabeledCount = (props: {
  delugeId: DelugeId
  label: string
  count: number
  countMinChars: number
  onClick: () => void
}) => (
  <LabeledCountRoot type="button" onClick={props.onClick}>
    <span>{props.label}</span>
    <LabeledCountCount $countMinChars={props.countMinChars}>
      ({props.count})
    </LabeledCountCount>
  </LabeledCountRoot>
)
LabeledCount.Skeleton = (props: { width: number }) => (
  <LabeledCountRoot>
    <Skeleton.Line width={props.width} />
  </LabeledCountRoot>
)

const LabeledCountRoot = styled.button`
  all: unset;
  cursor: pointer;
  text-align: center;
  font-weight: 500;
  font-size: 12px;
  color: inherit;
  text-transform: capitalize;
`

const LabeledCountCount = styled.span<{ $countMinChars: number }>`
  display: inline-block;
  min-width: ${(x) => x.$countMinChars}ch;
`

const Skeleton_ = (props: { withoutCoinsCount?: boolean }) => (
  <Root>
    <UserProfile>
      <div>
        <Skeleton variant={'circular'} width={24} height={24} />
      </div>
      <Name>
        <Skeleton.Line width={90} />
      </Name>
    </UserProfile>
    <Center>
      <LabeledCount.Skeleton width={90} />
      <Sep vertical />
      <LabeledCount.Skeleton width={85} />
      {props.withoutCoinsCount ? undefined : (
        <>
          <Sep vertical />
          <LabeledCount.Skeleton width={108} />
        </>
      )}
    </Center>
    <Right>
      <UnfollowSkeleton>
        <Skeleton.Surface />
      </UnfollowSkeleton>

      <KebabSkeleton>
        <Skeleton.Surface />
      </KebabSkeleton>
    </Right>
  </Root>
)

UserConnectionsItem.Skeleton = Skeleton_
const UnfollowSkeleton = styled.div`
  width: 62px;
  height: 32px;
  position: relative;
`
const KebabSkeleton = styled.div`
  width: 32px;
  height: 32px;
  position: relative;
`
